import type { CcFooterLayout } from "@/lib/cc-footer-layout";
import type { HeroTheme } from "@/lib/hero-theme";

export type ContactPageVariant =
  | "hero-banner"
  | "inset-frame"
  | "light-split"
  | "dark-split"
  | "parallax-panel";

export type ContactFormTheme = "light" | "dark";

export type ContactVariantConfig = {
  formTheme: ContactFormTheme;
  showExtendedFields: boolean;
  showBudgetFields: boolean;
};

export type ContactVariantChrome = {
  footerLayout: CcFooterLayout;
  heroTheme: HeroTheme;
};

/**
 * Contact page layout mode.
 * - `light-split`: plain light page — form left, email/call right.
 * - `dark-split`: split columns on antique hero; fields on hero; footer seam attached (default).
 * - `parallax-panel`: antique hero parallax + logo band + right form card.
 * - `hero-banner`: split hero (copy on image) + compact light form.
 * - `inset-frame`: full-bleed inset card on dark page padding (legacy).
 */
export const CONTACT_PAGE_VARIANT: ContactPageVariant = "dark-split";

/** Footer + home-hero tokens implied by each contact variant (single source of truth). */
export const CONTACT_VARIANT_CHROME: Record<ContactPageVariant, ContactVariantChrome> = {
  "light-split": { footerLayout: "article-detached", heroTheme: "light" },
  "dark-split": { footerLayout: "overview-attached", heroTheme: "dark" },
  "parallax-panel": { footerLayout: "overview-attached", heroTheme: "dark" },
  "hero-banner": { footerLayout: "overview-attached", heroTheme: "light" },
  "inset-frame": { footerLayout: "overview-attached", heroTheme: "dark" },
};

const VARIANT_CONFIG: Record<ContactPageVariant, ContactVariantConfig> = {
  "light-split": {
    formTheme: "light",
    showExtendedFields: true,
    showBudgetFields: false,
  },
  "dark-split": {
    formTheme: "dark",
    showExtendedFields: true,
    showBudgetFields: false,
  },
  "parallax-panel": {
    formTheme: "dark",
    showExtendedFields: false,
    showBudgetFields: false,
  },
  "hero-banner": {
    formTheme: "light",
    showExtendedFields: false,
    showBudgetFields: false,
  },
  "inset-frame": {
    formTheme: "light",
    showExtendedFields: true,
    showBudgetFields: true,
  },
};

export function resolveContactPageVariant(
  devOverride?: ContactPageVariant | null,
): ContactPageVariant {
  return devOverride ?? CONTACT_PAGE_VARIANT;
}

export function contactVariantChrome(
  variant: ContactPageVariant = CONTACT_PAGE_VARIANT,
): ContactVariantChrome {
  return CONTACT_VARIANT_CHROME[variant];
}

export function contactVariantConfig(
  variant: ContactPageVariant = CONTACT_PAGE_VARIANT,
): ContactVariantConfig {
  return VARIANT_CONFIG[variant];
}

/** Attached footer seam (links column only — no Odysseus closing CTA band). */
export function contactUsesAttachedFooterSeam(
  variant: ContactPageVariant = CONTACT_PAGE_VARIANT,
): boolean {
  return contactVariantChrome(variant).footerLayout === "overview-attached";
}

export function contactMainClassName(
  variant: ContactPageVariant = CONTACT_PAGE_VARIANT,
): string {
  const classes = ["cc-main", "cc-main--contact"];
  if (contactUsesAttachedFooterSeam(variant)) {
    classes.push("cc-main--footer-attached");
  }
  return classes.join(" ");
}

export function isSplitColumnContactVariant(
  variant: ContactPageVariant,
): boolean {
  return variant === "light-split" || variant === "dark-split";
}
